<template>
  <div class="d-flex flex-column gap-3">
    <div class="reservas-toolbar">
      <div class="reservas-toolbar-left">
        <div class="reservas-filtros">
          <!-- Deporte -->
          <select
            class="form-select form-select-sm"
            v-model.number="filtros.idDeporte"
          >
            <option
              v-for="d in deportes"
              :key="d.idDeporte"
              :value="d.idDeporte"
            >
              {{ d.descripcion }}
            </option>
          </select>

          <!-- Cancha -->
          <select class="form-select form-select-sm" v-model="filtros.idCancha">
            <option :value="null">Todas las canchas</option>
            <option
              v-for="c in canchasFiltradasPorDeporte"
              :key="c.idCancha"
              :value="c.idCancha"
            >
              {{ c.descripcion }}
            </option>
          </select>
        </div>

        <div class="reservas-fecha-duracion">
          <!-- Fecha -->
          <div class="input-group reservas-fecha">
            <button
              type="button"
              class="btn btn-outline-secondary d-flex align-items-center"
              title="Día anterior"
              @click="cambiarFechaReserva(-1)"
            >
              <span class="material-symbols-rounded">chevron_left</span>
            </button>

            <input
              type="date"
              class="form-control"
              v-model="filtros.fecha"
              @change="fetchReservas"
            />

            <button
              type="button"
              class="btn btn-outline-secondary d-flex align-items-center"
              title="Día siguiente"
              @click="cambiarFechaReserva(1)"
            >
              <span class="material-symbols-rounded">chevron_right</span>
            </button>
          </div>

          <!-- Duración -->
          <div class="btn-group btn-group-sm duracion-turnos" role="group">
            <button
              type="button"
              class="btn w-50"
              :class="
                configTurnos.duracionMin === 30
                  ? 'btn-success active'
                  : 'btn-outline-success'
              "
              @click="configTurnos.duracionMin = 30"
            >
              30 min
            </button>

            <button
              type="button"
              class="btn w-50"
              :class="
                configTurnos.duracionMin === 60
                  ? 'btn-success active'
                  : 'btn-outline-success'
              "
              @click="configTurnos.duracionMin = 60"
            >
              60 min
            </button>
          </div>
        </div>
      </div>

      <!-- Botón desktop/tablet -->
      <button
        class="btn btn-success d-flex align-items-center gap-1 btn-nueva-reserva-desktop"
        @click="abrirModalNuevaReserva"
      >
        <span class="material-symbols-rounded">add</span>
        Nueva reserva
      </button>

      <!-- Botón mobile flotante -->
      <button
        class="btn btn-success btn-nueva-reserva-mobile"
        @click="abrirModalNuevaReserva"
        title="Nueva reserva"
      >
        +
      </button>
    </div>

    <!-- Grilla visual -->
    <div class="reservas-grid-wrapper border rounded">
      <div class="reservas-grid-scroll">
        <table
          class="table table-sm mb-0 reservas-grid align-middle text-center"
        >
          <thead class="table-light">
            <tr>
              <th class="text-start sticky-col">Hora</th>
              <th
                v-for="c in canchasFiltradas"
                :key="c.idCancha"
                class="text-center"
              >
                {{ c.descripcion }}
              </th>
            </tr>
          </thead>

          <tbody>
            <tr v-for="(slot, idxSlot) in slots" :key="slot">
              <!-- Columna izquierda: hora/slot -->
              <td class="text-start fw-semibold sticky-col">
                {{ slot }}
              </td>

              <!-- Celdas de canchas -->
              <td
                v-for="c in canchasFiltradas"
                :key="c.idCancha + '-' + slot"
                class="reservas-cell"
                :class="{
                  'reservas-cell--continuacion': esContinuacionReserva(
                    c.idCancha,
                    slot,
                    idxSlot,
                  ),
                }"
              >
                <!-- Fondo de la celda: muestra únicamente los bloques de 30 min libres -->
                <div
                  class="reserva-empty"
                  :class="{
                    'reserva-empty--split': configTurnos.duracionMin === 60,
                  }"
                >
                  <template v-if="configTurnos.duracionMin === 60">
                    <button
                      v-if="!bloqueHorarioOcupado(c.idCancha, slot, 30)"
                      type="button"
                      class="reserva-empty-half"
                      :title="`Reservar desde las ${slot}`"
                      @click.stop="abrirModalNuevaReservaDesdeGrilla(c, slot)"
                    >
                      <span>{{ slot }}</span>
                    </button>
                    <div
                      v-else
                      class="reserva-empty-half reserva-empty-half--ocupado"
                    ></div>

                    <button
                      v-if="
                        !bloqueHorarioOcupado(
                          c.idCancha,
                          sumarMinutosHora(slot, 30),
                          30,
                        )
                      "
                      type="button"
                      class="reserva-empty-half"
                      :title="`Reservar desde las ${sumarMinutosHora(
                        slot,
                        30,
                      )}`"
                      @click.stop="
                        abrirModalNuevaReservaDesdeGrilla(
                          c,
                          sumarMinutosHora(slot, 30),
                        )
                      "
                    >
                      <span>{{ sumarMinutosHora(slot, 30) }}</span>
                    </button>
                    <div
                      v-else
                      class="reserva-empty-half reserva-empty-half--ocupado"
                    ></div>
                  </template>

                  <template v-else>
                    <button
                      v-if="
                        !bloqueHorarioOcupado(
                          c.idCancha,
                          slot,
                          configTurnos.duracionMin,
                        )
                      "
                      type="button"
                      class="reserva-empty-full"
                      :title="`Reservar desde las ${slot}`"
                      @click.stop="abrirModalNuevaReservaDesdeGrilla(c, slot)"
                    >
                      <span>{{ slot }}</span>
                    </button>
                  </template>
                </div>

                <!-- Las reservas se dibujan por encima del fondo -->
                <div
                  v-for="r in reservasEnSlot(c.idCancha, slot)"
                  :key="r.idReserva"
                  :class="[
                    'reserva-block',
                    {
                      'reserva-block--start': esInicioReservaR(
                        normalizarReserva(r),
                        slot,
                      ),
                      'reserva-block--end': esFinReservaR(
                        normalizarReserva(r),
                        slot,
                      ),
                      'reserva-block--single':
                        esInicioReservaR(normalizarReserva(r), slot) &&
                        esFinReservaR(normalizarReserva(r), slot),
                    },
                  ]"
                  :style="getReservaStylesR(normalizarReserva(r), slot)"
                  :title="buildTooltip(normalizarReserva(r))"
                  @click.stop="abrirModalEditarReserva(normalizarReserva(r))"
                >
                  <!-- Nombre solo en el slot donde empieza la reserva -->
                  <template v-if="esInicioReservaR(normalizarReserva(r), slot)">
                    <div class="reserva-titulo">
                      {{ normalizarReserva(r).cliente || "" }}
                      <span
                        v-if="
                          r.pagado === 1 && r.monto_pagado >= r.monto_a_pagar
                        "
                        class="material-symbols-rounded simbolo_pagado"
                      >
                        paid
                      </span>
                    </div>
                  </template>

                  <!-- Horario solo en el slot donde termina la reserva -->
                  <template v-if="esFinReservaR(normalizarReserva(r), slot)">
                    <div class="reserva-detalle">
                      <span v-if="mostrarHorarioReserva">
                        De {{ normalizarReserva(r).horaInicio }} hasta
                        {{ normalizarReserva(r).horaFin }}
                      </span>
                      <span
                        v-if="
                          r.pagado === 1 && r.monto_pagado >= r.monto_a_pagar
                        "
                      >
                        (Pagado: {{ formatearPrecio(r.monto_pagado) }})</span
                      >
                      <span
                        v-else-if="
                          r.pagado === 1 && r.monto_pagado < r.monto_a_pagar
                        "
                        ><b>
                          <em>
                            (Pagado: {{ formatearPrecio(r.monto_pagado) }} de
                            {{ formatearPrecio(r.monto_a_pagar) }})</em
                          ></b
                        >
                      </span>
                      <span v-else
                        ><b
                          ><em>
                            (A pagar:
                            {{ formatearPrecio(r.monto_a_pagar) }})</em
                          ></b
                        ></span
                      >
                    </div>
                  </template>
                </div>
                <div
                  v-for="r in reservasEnSlot(c.idCancha, slot)"
                  :key="r.idReserva"
                  :class="[
                    'reserva-block',
                    {
                      'reserva-block--start': esInicioReservaR(
                        normalizarReserva(r),
                        slot,
                      ),
                      'reserva-block--end': esFinReservaR(
                        normalizarReserva(r),
                        slot,
                      ),
                      'reserva-block--single':
                        esInicioReservaR(normalizarReserva(r), slot) &&
                        esFinReservaR(normalizarReserva(r), slot),
                    },
                  ]"
                  :style="getReservaStylesR(normalizarReserva(r), slot)"
                  :title="buildTooltip(normalizarReserva(r))"
                  @click.stop="abrirModalEditarReserva(normalizarReserva(r))"
                >
                  <!-- Nombre solo en el slot donde empieza la reserva -->
                  <template v-if="esInicioReservaR(normalizarReserva(r), slot)">
                    <div class="reserva-titulo">
                      {{ normalizarReserva(r).cliente || "" }}
                      <span
                        v-if="
                          r.pagado === 1 && r.monto_pagado >= r.monto_a_pagar
                        "
                        class="material-symbols-rounded simbolo_pagado"
                      >
                        paid
                      </span>
                    </div>
                  </template>

                  <!-- Horario solo en el slot donde termina la reserva -->
                  <template v-if="esFinReservaR(normalizarReserva(r), slot)">
                    <div class="reserva-detalle">
                      <span v-if="mostrarHorarioReserva">
                        De {{ normalizarReserva(r).horaInicio }} hasta
                        {{ normalizarReserva(r).horaFin }}
                      </span>
                      <span
                        v-if="
                          r.pagado === 1 && r.monto_pagado >= r.monto_a_pagar
                        "
                      >
                        (Pagado: {{ formatearPrecio(r.monto_pagado) }})</span
                      >
                      <span
                        v-else-if="
                          r.pagado === 1 && r.monto_pagado < r.monto_a_pagar
                        "
                        ><b>
                          <em>
                            (Pagado: {{ formatearPrecio(r.monto_pagado) }} de
                            {{ formatearPrecio(r.monto_a_pagar) }})</em
                          ></b
                        >
                      </span>
                      <span v-else
                        ><b
                          ><em>
                            (A pagar:
                            {{ formatearPrecio(r.monto_a_pagar) }})</em
                          ></b
                        ></span
                      >
                    </div>
                  </template>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Modal NUEVA RESERVA (panel derecho) -->
    <BaseModal
      v-model="modalNuevaAbierta"
      :title="estaEditando ? 'Editar Reserva' : 'Nueva reserva'"
      :ok-label="estaEditando ? 'Guardar' : 'Guardar'"
      cancel-label="Cancelar"
      :loading="guardandoReserva"
      panel-class="fo-panel-right"
      @confirm="onValidarReserva"
      @cancel="onCancelModalReserva"
    >
      <div class="reserva-modal-scroll">
        <form
          @submit.prevent="onValidarReserva"
          class="d-flex flex-column gap-2"
        >
          <!-- Deporte + Día -->
          <div class="row g-2">
            <div class="col-sm-6">
              <label class="form-label">Deporte</label>
              <select
                class="form-select form-select-sm"
                v-model="formReserva.idDeporte"
              >
                <option
                  v-for="d in deportes"
                  :key="d.idDeporte"
                  :value="d.idDeporte"
                >
                  {{ d.descripcion }}
                </option>
              </select>
            </div>

            <div class="col-sm-6">
              <label class="form-label">Día</label>
              <input
                type="date"
                class="form-control form-control-sm"
                v-model="formReserva.fecha"
              />
            </div>
          </div>

          <div class="row g-2">
            <div class="col-12">
              <label class="form-label">Cancha</label>
              <select
                class="form-select form-select-sm"
                v-model.number="formReserva.idCancha"
                title="Si la cancha elegida no está disponible, el sistema te va a sugerir otra antes de grabar."
              >
                <option :value="null">Sugerir automáticamente</option>
                <option
                  v-for="c in canchasModal"
                  :key="c.idCancha"
                  :value="c.idCancha"
                >
                  {{ c.descripcion }}
                </option>
              </select>
            </div>
          </div>

          <div class="row g-2">
            <!-- HORA DESDE -->
            <div class="col-md-6">
              <label class="form-label">Hora desde</label>

              <div class="d-flex gap-2">
                <!-- Horas -->
                <select
                  class="form-select form-select-sm"
                  v-model.number="horaDesdeHora"
                >
                  <option v-for="h in 24" :key="'hdh' + h" :value="h - 1">
                    {{ String(h - 1).padStart(2, "0") }}
                  </option>
                </select>

                <!-- Minutos -->
                <select
                  class="form-select form-select-sm"
                  v-model.number="horaDesdeMin"
                >
                  <option v-for="m in minutosTurno" :key="'hdm' + m" :value="m">
                    {{ String(m).padStart(2, "0") }}
                  </option>
                </select>
              </div>
            </div>

            <!-- HORA HASTA -->
            <div class="col-md-6">
              <label class="form-label">Hora hasta</label>

              <div class="d-flex gap-2">
                <!-- Horas -->
                <select
                  class="form-select form-select-sm"
                  v-model.number="horaHastaHora"
                >
                  <option v-for="h in 24" :key="'hhh' + h" :value="h - 1">
                    {{ String(h - 1).padStart(2, "0") }}
                  </option>
                </select>

                <!-- Minutos -->
                <select
                  class="form-select form-select-sm"
                  v-model.number="horaHastaMin"
                >
                  <option v-for="m in minutosTurno" :key="'hhm' + m" :value="m">
                    {{ String(m).padStart(2, "0") }}
                  </option>
                </select>
              </div>
            </div>
          </div>

          <!-- Generar abono -->
          <div class="form-check mt-1">
            <input
              class="form-check-input"
              type="checkbox"
              id="chk-abono"
              v-model="formReserva.generarAbono"
            />
            <label class="form-check-label" for="chk-abono">
              Generar abono semanal
            </label>
          </div>

          <hr />

          <!-- Cliente -->
          <div class="d-flex justify-content-between align-items-center mb-1">
            <h6 class="mb-0">Datos del cliente</h6>
          </div>

          <!-- Renglón 1: Teléfono + Email -->
          <div class="row g-2 align-items-end">
            <div :class="usaEmailCliente ? 'col-md-6' : 'col-12'">
              <label class="form-label">Teléfono*</label>
              <input
                type="tel"
                class="form-control form-control-sm"
                v-model="formReserva.clienteTelefono"
                @input="dispararBusquedaCliente"
                autocomplete="off"
              />
            </div>

            <div v-if="usaEmailCliente" class="col-md-6">
              <div
                class="d-flex justify-content-between align-items-center mb-1"
              >
                <label class="form-label mb-0" for="txt-email">Email</label>

                <div v-if="false" class="form-check form-switch m-0">
                  <!--TODO:: Habilitar aviso de email con los datos de la reserva-->
                  <input
                    class="form-check-input"
                    type="checkbox"
                    id="chk-enviar-email"
                    v-model="formReserva.enviarAvisoEmail"
                  />
                  <label class="form-check-label" for="chk-enviar-email">
                    Enviar reserva
                  </label>
                </div>
              </div>

              <input
                id="txt-email"
                type="email"
                class="form-control form-control-sm"
                v-model="formReserva.clienteEmail"
                @input="dispararBusquedaCliente"
                autocomplete="off"
              />
            </div>
          </div>

          <!-- Renglón 2: Nombre + Tipo de cliente -->
          <div class="row g-2 align-items-end position-relative mt-1">
            <div class="col-md-6">
              <label class="form-label">Nombre*</label>
              <input
                type="text"
                class="form-control form-control-sm"
                v-model="formReserva.clienteNombre"
                @input="dispararBusquedaCliente"
                @keydown="manejarKeydownCliente"
                autocomplete="off"
              />

              <!-- Dropdown sugerencias -->
              <ul
                v-if="mostrandoSugerenciasCliente && resultadosCliente.length"
                class="lista-sugerencias-cliente"
              >
                <li
                  v-for="(c, idx) in resultadosCliente"
                  :key="c.id_cliente"
                  :class="[
                    'item-sugerencia-cliente',
                    {
                      'item-sugerencia-cliente--activo':
                        idx === indiceClienteActivo,
                    },
                  ]"
                  @mousedown.prevent="seleccionarCliente(c)"
                >
                  <div class="fw-semibold">
                    {{ c.nombre }}
                  </div>
                  <div class="small text-muted">
                    {{ c.celular || "Sin teléfono" }}
                    <span v-if="usaEmailCliente && c.email"> · {{ c.email }}</span>
                  </div>
                </li>
              </ul>
            </div>

            <div class="col-md-6">
              <label class="form-label">Tipo de cliente</label>
              <select
                class="form-select form-select-sm"
                v-model="formReserva.tipoCliente"
              >
                <option v-for="t in tipos_cliente" :key="t.id" :value="t.id">
                  {{ t.descripcion }}
                </option>
              </select>
            </div>
          </div>

          <!-- Observaciones -->
          <div class="mt-3">
            <label class="form-label">Observaciones</label>
            <textarea
              class="form-control form-control-sm"
              rows="1"
              v-model="formReserva.observaciones"
            ></textarea>
          </div>

          <!-- Pago (solo cuando se edita) -->
          <div v-if="estaEditando" class="mt-2">
            <hr class="my-2" />

            <!-- Renglón 1: Pagado -->
            <div class="row g-2 align-items-center">
              <div class="col-12">
                <div class="form-check form-switch m-0">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    id="chk-pagado"
                    v-model="pagoPagado"
                    @change="onChangePagadoReserva"
                  />
                  <label class="form-check-label" for="chk-pagado">
                    Pagado
                  </label>
                </div>
              </div>
            </div>

            <!-- Renglón 2: Forma de pago + Monto + botón -->
            <div v-if="pagoPagado" class="row g-2 align-items-end mt-2">
              <div class="col-md-6">
                <label class="form-label">Forma de pago</label>
                <select
                  class="form-select form-select-sm"
                  v-model.number="formReserva.idFormaPago"
                  @change="onChangeFormaPagoReserva"
                >
                  <option
                    v-for="fp in formasPagoConfig"
                    :key="fp.idFormaPago"
                    :value="fp.idFormaPago"
                  >
                    {{ fp.descripcion }}
                    <template v-if="fp.importeAjuste > 0">
                      (+{{ formatearPrecio(fp.importeAjuste) }})
                    </template>
                  </option>
                </select>
              </div>

              <div class="col-md-5">
                <label class="form-label">Monto a pagar</label>
                <div class="input-group input-group-sm">
                  <span class="input-group-text">$</span>
                  <input
                    type="number"
                    class="form-control text-end"
                    inputmode="decimal"
                    min="0"
                    step="1000"
                    v-model.number="pagoMontoPagado"
                  />
                </div>

                <small
                  v-if="false && formaPagoSeleccionadaConfig"
                  class="text-muted"
                >
                  Ajuste:
                  {{
                    formatearPrecio(formaPagoSeleccionadaConfig.importeAjuste)
                  }}
                </small>
              </div>

              <div class="col-md-1 d-grid">
                <button
                  type="button"
                  class="btn btn-success btn-sm d-flex align-items-center justify-content-center"
                  :disabled="guardandoPago || !formReserva.idFormaPago"
                  @click="guardarPagoReserva"
                  title="Guardar pago"
                >
                  <span class="material-symbols-rounded">done</span>
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
      <template #footer-left>
        <div v-if="estaEditando" class="d-flex gap-2">
          <button
            type="button"
            class="btn btn-outline-success d-inline-flex align-items-center gap-1"
            @click="abrirSelectorWhatsApp"
          >
            <WhatsAppIcon />
          </button>
          <button
            type="button"
            class="btn btn-outline-danger"
            @click="onEliminarReserva"
          >
            Eliminar reserva
          </button>
        </div>
      </template>
    </BaseModal>
  </div>

  <BaseModal
    v-model="modalWhatsAppPlantillas"
    title="Elegir mensaje de WhatsApp"
    layout="modal"
    cancel-label="Cerrar"
    @cancel="modalWhatsAppPlantillas = false"
  >
    <div v-if="cargandoMensajesWhatsApp" class="text-center py-4">
      Cargando mensajes…
    </div>
    <div
      v-else-if="mensajesWhatsApp.length === 0"
      class="alert alert-warning mb-0"
    >
      No hay plantillas activas configuradas.
    </div>
    <div v-else class="d-flex flex-column gap-2">
      <button
        v-for="m in mensajesWhatsApp"
        :key="m.id_mensaje_whatsapp"
        type="button"
        class="btn btn-outline-success text-start p-3"
        @click="seleccionarMensajeWhatsApp(m)"
      >
        <div class="fw-semibold d-flex align-items-center gap-1">
          <WhatsAppIcon class="text-success" />
          {{ m.nombre }}
        </div>
        <div class="small text-muted wa-template-preview">{{ m.mensaje }}</div>
      </button>
    </div>
    <template #footer
      ><button
        class="btn btn-outline-secondary"
        @click="modalWhatsAppPlantillas = false"
      >
        Cerrar
      </button></template
    >
  </BaseModal>

  <BaseModal
    v-model="modalWhatsAppPreview"
    title="Vista previa de WhatsApp"
    layout="modal"
    ok-label="Abrir WhatsApp"
    cancel-label="Volver"
    @confirm="abrirWhatsApp"
    @cancel="volverAPlantillasWhatsApp"
  >
    <div class="mb-2">
      <label class="form-label">Teléfono</label>
      <input class="form-control" v-model="telefonoWhatsApp" />
      <div class="form-text">
        Se abrirá WhatsApp con este número y el mensaje listo para enviar.
      </div>
    </div>
    <div>
      <label class="form-label">Mensaje</label>
      <textarea
        class="form-control wa-mensaje-editable"
        rows="12"
        v-model="mensajeWhatsAppPreview"
      ></textarea>
      <div class="form-text">
        Podés editar el texto antes de abrir WhatsApp.
      </div>
    </div>
  </BaseModal>

  <MostrarMensaje
    v-model:modelValue="mostrarMensajeOk"
    status="success"
    :message="mensajeOk"
    :duration="3000"
    :closable="true"
  />

  <MostrarMensaje
    v-model:modelValue="mostrarMensajeError"
    status="error"
    :message="mensajeError"
    :duration="duracionMensajeError"
    :closable="true"
  />

  <MostrarMensaje
    v-model:modelValue="mostrarMensajeConfirmacion"
    status="info"
    mode="confirm"
    :duration="0"
    :closable="true"
    ok-label="Confirmar"
    cancel-label="Cancelar"
    :message="mensajeConfirmacion"
    @confirm="onConfirmReserva"
    @cancel="onCancelModalConfirm"
  />
</template>

<script setup>
import { computed, ref, watch, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import BaseModal from "@/components/BaseModal.vue";
import WhatsAppIcon from "@/components/WhatsAppIcon.vue";
import { usaEmailCliente } from "@/config/features";
import MostrarMensaje from "@/components/MostrarMensaje.vue";
import { sumarDias, fechaHoyAR } from "@/utils/fecha";
import { formatearPrecio } from "@/utils/numeros";
import {
  apiGetDeportesActivos,
  apiGetTipoCliente,
  apiGetCanchasPorDeporte,
  apiPostGrabarReserva,
  apiGetReservas,
  apiGetRangoHorario,
  apiGetObtenerCanchaHorarioPrecio,
  apiGetValidarReserva,
  apiGetParametroGeneralPorClave,
  apiGetFormasPago,
  apiGetFormaPagoConfigVigente,
  apiBuscarCliente,
  apiPutReserva,
  apiPutReservaPagada,
  apiGetMensajesWhatsAppActivos,
} from "@/api";

let controller = null;

const route = useRoute();
const router = useRouter();

const idDeporteInicial = computed(() => {
  const raw = route.query.idDeporte;
  if (!raw) return null;

  const num = Number(raw);
  return Number.isNaN(num) ? null : num;
});

try {
  controller?.abort?.();
} catch {}
controller =
  typeof AbortController !== "undefined" ? new AbortController() : null;

onMounted(async () => {
  await cargarDeportes();
  await cargarTiposCliente();
  await cargarFormasPago();
  await cargarFechaMaximaReserva();
  await cargarParametroMostrarHorarioReserva();

  if (filtros.value.idDeporte) {
    await cargarCanchas();
    await cargarRangoHorario();
    await cargarReservas();
  }
});

const deportes = ref([]);
const canchas = ref([]);

const tipos_cliente = ref([]);

const mostrarMensajeError = ref(false);
const mensajeError = ref("");
const mostrarMensajeOk = ref(false);
const mensajeOk = ref("");
const duracionMensajeError = ref(5000);
const precio = ref(0);
const mensajeConfirmacion = ref("");
const mostrarMensajeConfirmacion = ref(false);
const esBajaReserva = ref(false);
const fechaMaximaModificacion = ref(null);
const mostrarHorarioReserva = ref(true);

const canchaValidadaPendiente = ref(null);
const precioValidadoPendiente = ref(null);

const pagoPagado = ref(false);
const pagoMontoAPagar = ref(null);
const pagoMontoPagado = ref(null);
const guardandoPago = ref(false);

const formasPagoConfig = ref([]);
const cargandoFormasPago = ref(false);

const modalWhatsAppPlantillas = ref(false);
const modalWhatsAppPreview = ref(false);
const cargandoMensajesWhatsApp = ref(false);
const mensajesWhatsApp = ref([]);
const mensajeWhatsAppPreview = ref("");
const telefonoWhatsApp = ref("");

const deporteSeleccionado = computed(() => {
  return (
    deportes.value.find((d) => d.idDeporte === formReserva.value.idDeporte) ||
    null
  );
});

const minDivisionTurno = computed(() => {
  return Number(deporteSeleccionado.value?.minDivisionTurno ?? 5);
});

const minutosTurno = computed(() => {
  const division = minDivisionTurno.value;

  const minutos = [];

  for (let minuto = 0; minuto < 60; minuto += division) {
    minutos.push(minuto);
  }

  return minutos;
});

const hoy = fechaHoyAR();

const filtros = ref({
  idDeporte: idDeporteInicial.value ?? null,
  idCancha: null,
  fecha: hoy,
});

const configTurnos = ref({
  horaDesde: "08:00",
  horaHasta: "23:00",
  duracionMin: 60,
});

/**
 * 🔹 GENERAR SLOTS DINÁMICOS SEGÚN DURACIÓN
 */
const slots = computed(() => {
  const lista = [];

  const [startH, startM] = configTurnos.value.horaDesde.split(":").map(Number);
  const [endH, endM] = configTurnos.value.horaHasta.split(":").map(Number);
  const step = configTurnos.value.duracionMin;

  let h = startH;
  let m = startM;

  while (h < endH || (h === endH && m < endM)) {
    const hh = String(h).padStart(2, "0");
    const mm = String(m).padStart(2, "0");
    lista.push(`${hh}:${mm}`);

    m += step;
    if (m >= 60) {
      h += Math.floor(m / 60);
      m = m % 60;
    }
  }

  return lista;
});

/**
 * 🔹 Filtrado de canchas según deporte / cancha
 */
const canchasFiltradasPorDeporte = computed(() => {
  if (!filtros.value.idDeporte) return canchas.value;
  return canchas.value.filter((c) => c.idDeporte === filtros.value.idDeporte);
});

const canchasFiltradas = computed(() => {
  let result = canchasFiltradasPorDeporte.value;
  if (filtros.value.idCancha) {
    result = result.filter((c) => c.idCancha === filtros.value.idCancha);
  }
  return result;
});

const canchasModal = computed(() => {
  if (!formReserva.value.idDeporte) return [];
  return canchas.value.filter(
    (c) => c.idDeporte === formReserva.value.idDeporte,
  );
});

const getDescripcionCancha = (idCancha) => {
  const cancha = canchas.value.find((c) => c.idCancha === idCancha);
  return cancha?.descripcion || `Cancha ${idCancha}`;
};

const estaEditando = computed(() => !!formReserva.value.idReserva);

const reservas = ref([]);

const normalizarHoraValidacion = (valor) => {
  if (valor == null || valor === "") return "";

  if (valor instanceof Date && !Number.isNaN(valor.getTime())) {
    const h = String(valor.getHours()).padStart(2, "0");
    const m = String(valor.getMinutes()).padStart(2, "0");
    return `${h}:${m}`;
  }

  const texto = String(valor).trim();

  // Soporta valores como:
  // "09:00", "09:00:00", "1900-01-01T09:00:00", "1900-01-01 09:00:00"
  const match = texto.match(/(\d{1,2}):(\d{2})(?::\d{2})?/);
  if (!match) return "";

  return `${String(match[1]).padStart(2, "0")}:${match[2]}`;
};

const toMinutes = (hhmm) => {
  const hora = normalizarHoraValidacion(hhmm);
  if (!hora) return null;

  const [h, m] = hora.split(":").map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  return h * 60 + m;
};

const buildDateTimeLocal = (dateStr, timeStr) => {
  if (!dateStr || !timeStr) return null;
  return `${dateStr}T${timeStr}:00`;
};

const getIdDiaDesdeFecha = (fechaStr) => {
  if (!fechaStr) return null;
  const [y, m, d] = fechaStr.split("-").map(Number);
  if (!y || !m || !d) return null;

  // Usamos fecha local para evitar que el timezone cambie el día.
  const jsDay = new Date(y, m - 1, d).getDay(); // 0 Domingo, 1 Lunes...
  return jsDay === 0 ? 7 : jsDay; // Sistema: 1 Lunes ... 7 Domingo
};

const obtenerAdvertenciaHorarioConfigurado = async ({
  idCancha,
  fecha,
  horaDesde,
  horaHasta,
}) => {
  if (!idCancha || !fecha || !horaDesde || !horaHasta) return "";

  try {
    const resp = await apiGetObtenerCanchaHorarioPrecio(
      { idCancha },
      { signal: controller?.signal },
    );

    const idDia = getIdDiaDesdeFecha(fecha);
    const configuracionDia = (resp?.configuracionPorDia ?? []).find(
      (x) => Number(x.dia) === Number(idDia),
    );

    if (!configuracionDia || !configuracionDia.abierto) {
      return `\n\n⚠️ Atención: la cancha ${getDescripcionCancha(
        idCancha,
      )} no tiene horario configurado para ese día. Podés confirmar igual si querés cargar una excepción.`;
    }

    const minDesdeReserva = toMinutes(horaDesde);
    const minHastaReserva = toMinutes(horaHasta);

    const estaDentroDeHorario = (configuracionDia.horarios ?? []).some((h) => {
      const minDesdeHorario = toMinutes(h.desde);
      const minHastaHorario = toMinutes(h.hasta);

      return (
        minDesdeReserva != null &&
        minHastaReserva != null &&
        minDesdeHorario != null &&
        minHastaHorario != null &&
        minDesdeReserva >= minDesdeHorario &&
        minHastaReserva <= minHastaHorario
      );
    });

    if (!estaDentroDeHorario) {
      const horariosTxt = (configuracionDia.horarios ?? [])
        .map(
          (h) =>
            `${normalizarHoraValidacion(h.desde)} a ${normalizarHoraValidacion(
              h.hasta,
            )}`,
        )
        .join(", ");

      return (
        `\n\n⚠️ Atención: el horario ${horaDesde} a ${horaHasta} está fuera del horario configurado para ${getDescripcionCancha(
          idCancha,
        )}` +
        (horariosTxt ? ` (${horariosTxt}).` : ".") +
        " Podés confirmar igual si querés cargar una excepción."
      );
    }
  } catch (err) {
    console.error("Error validando horario configurado de la cancha", err);
  }

  return "";
};

// ¿El slot pertenece a esa reserva?
const slotEnReserva = (reserva, slot) => {
  const horaBase = reserva.horaInicio || reserva.hora;
  const inicioReserva = toMinutes(horaBase);
  const inicioSlot = toMinutes(slot);

  if (inicioReserva == null || inicioSlot == null) return false;

  const durReserva = reserva.duracionMin ?? configTurnos.value.duracionMin;
  const durSlot = configTurnos.value.duracionMin;

  const finReserva = inicioReserva + durReserva;
  const finSlot = inicioSlot + durSlot;

  return inicioSlot < finReserva && finSlot > inicioReserva;
};

/**
 * 🔹 Reservas que ocupan un slot/cancha
 */
const reservasEnSlot = (idCancha, slot) => {
  return reservas.value
    .filter(
      (r) =>
        r &&
        r.idCancha === idCancha &&
        r.fecha === filtros.value.fecha &&
        slotEnReserva(r, slot),
    )
    .sort((a, b) => {
      const tA = toMinutes(a.horaInicio || a.hora);
      const tB = toMinutes(b.horaInicio || b.hora);
      return (tA ?? 0) - (tB ?? 0);
    });
};

/**
 * Normaliza una reserva con horaInicio/horaFin calculadas
 */
const normalizarReserva = (r) => {
  if (!r) return {};

  const horaInicio = r.horaInicio || r.hora;
  const minInicio = toMinutes(horaInicio);
  const duracion = r.duracionMin ?? configTurnos.value.duracionMin;
  const horaFinMin = minInicio + duracion;

  const hh = String(Math.floor(horaFinMin / 60)).padStart(2, "0");
  const mm = String(horaFinMin % 60).padStart(2, "0");
  const horaFin = `${hh}:${mm}`;

  const tipoCliente = r.tipoCliente ?? null;
  const tipoClienteLabel = "AAAA"; // placeholder

  return {
    ...r,
    horaInicio,
    horaFin,
    tipoCliente,
    tipoClienteLabel,
    observaciones: r.observaciones ?? "", // 👈 acá agregamos las obs
  };
};

/**
 * Inicio y fin de reserva en un slot (por reserva concreta)
 */
const esInicioReservaR = (reserva, slot) => {
  if (!reserva?.idReserva) return false;

  const horaBase = reserva.horaInicio || reserva.hora;
  const inicioReserva = toMinutes(horaBase);
  const inicioSlot = toMinutes(slot);
  const durSlot = configTurnos.value.duracionMin;
  if (inicioReserva == null || inicioSlot == null) return false;

  const finSlot = inicioSlot + durSlot;

  return (
    slotEnReserva(reserva, slot) &&
    inicioSlot <= inicioReserva &&
    inicioReserva < finSlot
  );
};

const esFinReservaR = (reserva, slot) => {
  if (!reserva?.idReserva) return false;

  const horaBase = reserva.horaInicio || reserva.hora;
  const inicioReserva = toMinutes(horaBase);
  const inicioSlot = toMinutes(slot);
  if (inicioReserva == null || inicioSlot == null) return false;

  const durReserva = reserva.duracionMin ?? configTurnos.value.duracionMin;
  const durSlot = configTurnos.value.duracionMin;

  const finReserva = inicioReserva + durReserva;
  const finSlot = inicioSlot + durSlot;

  return (
    slotEnReserva(reserva, slot) &&
    inicioSlot < finReserva &&
    finReserva <= finSlot
  );
};

/**
 * Calcula cuánto "baja" y cuánto "sube" el bloque en el slot (por reserva)
 */
const getReservaStylesR = (reserva, slot) => {
  if (!reserva?.idReserva) return {};

  const horaBase = reserva.horaInicio || reserva.hora;
  const inicioReserva = toMinutes(horaBase);
  const inicioSlot = toMinutes(slot);
  if (inicioReserva == null || inicioSlot == null) return {};

  const durReserva = reserva.duracionMin ?? configTurnos.value.duracionMin;
  const durSlot = configTurnos.value.duracionMin;

  const finReserva = inicioReserva + durReserva;
  const finSlot = inicioSlot + durSlot;

  let topPct = 0;
  let bottomPct = 0;

  if (esInicioReservaR(reserva, slot)) {
    const diff = Math.max(0, inicioReserva - inicioSlot);
    topPct = (diff / durSlot) * 100;
  }

  if (esFinReservaR(reserva, slot)) {
    const diff = Math.max(0, finSlot - finReserva);
    bottomPct = (diff / durSlot) * 100;
  }

  topPct = Math.min(Math.max(topPct, 0), 100);
  bottomPct = Math.min(Math.max(bottomPct, 0), 100);

  return {
    top: `${topPct}%`,
    bottom: `${bottomPct}%`,
  };
};

/**
 * Marca si la celda es continuación de la misma reserva que el slot anterior
 */
const esContinuacionReserva = (idCancha, slot, idxSlot) => {
  if (idxSlot === 0) return false;

  const slotAnterior = slots.value[idxSlot - 1];

  const actuales = reservasEnSlot(idCancha, slot);
  const anteriores = reservasEnSlot(idCancha, slotAnterior);

  return actuales.some((rAct) =>
    anteriores.some((rAnt) => rAnt.idReserva === rAct.idReserva),
  );
};

const buildTooltip = (reserva) => {
  if (!reserva || !reserva.idReserva) return "";

  const partes = [];

  if (reserva.cliente) {
    partes.push(reserva.cliente);
  }

  if (reserva.horaInicio && reserva.horaFin) {
    partes.push(`De ${reserva.horaInicio} a ${reserva.horaFin}`);
  }

  if (reserva.observaciones) {
    partes.push(reserva.observaciones);
  }

  return partes.join(" · ");
};

/**
 * 🔹 Modal Nueva reserva
 */
const modalNuevaAbierta = ref(false);
const guardandoReserva = ref(false);

const formReserva = ref({
  idDeporte: null,
  idCancha: null,
  fecha: hoy,
  horaDesde: "10:00",
  horaHasta: "11:00",
  generarAbono: false,
  clienteNombre: "",
  clienteTelefono: "",
  clienteEmail: "",
  enviarAvisoEmail: false,
  tipoCliente: 1,
  observaciones: "",
  idCliente: null,
  baja_fecha: null,
  idFormaPago: null,
});

const horaDesdeHora = ref(Number(formReserva.value.horaDesde.split(":")[0]));
const horaDesdeMin = ref(Number(formReserva.value.horaDesde.split(":")[1]));

const horaHastaHora = ref(Number(formReserva.value.horaHasta.split(":")[0]));
const horaHastaMin = ref(Number(formReserva.value.horaHasta.split(":")[1]));

/* Carga de datos */
const cargarParametroMostrarHorarioReserva = async () => {
  try {
    const resp = await apiGetParametroGeneralPorClave(
      "muestra_horario_reserva",
      {
        signal: controller?.signal,
      },
    );

    mostrarHorarioReserva.value = String(resp?.valor_caracter ?? "S") === "S";
  } catch (e) {
    console.error("Error al buscar parámetro muestra_horario_reserva", e);

    // Si falla, lo dejamos visible para no ocultar información por error.
    mostrarHorarioReserva.value = true;
  }
};

const cargarFechaMaximaReserva = async () => {
  let dias = 30;
  try {
    const resp = await apiGetParametroGeneralPorClave("dias_generar_abono", {
      signal: controller?.signal,
    });

    if (resp) {
      dias = resp.valor_numerico;
    }
  } catch (e) {
  }
  fechaMaximaModificacion.value = sumarDias(new Date(), dias);
};

const cargarDeportes = async () => {
  try {
    const data = await apiGetDeportesActivos({ signal: controller?.signal });
    const lista = Array.isArray(data)
      ? data
      : data?.results ?? data?.items ?? [];

    deportes.value = lista.map((s, i) => ({
      idDeporte: s.id_deporte ?? s.idDeporte ?? i,
      descripcion: s.descripcion ?? s.nombre ?? "",
      nombre: s.nombre ?? s.descripcion ?? "",
      imagen: s.nombre_imagen ?? null,
      slug: s.slug ?? "",
      minDivisionTurno: Number(s.min_division_turno ?? 5),
    }));

    // Si vino un idDeporte por la URL y existe en la lista, lo usamos
    if (!filtros.value.idDeporte && idDeporteInicial.value) {
      const existe = deportes.value.some(
        (d) => d.idDeporte === idDeporteInicial.value,
      );
      if (existe) {
        filtros.value.idDeporte = idDeporteInicial.value;
      }
    }

    // Si todavía no hay deporte seleccionado, caemos al primero
    if (!filtros.value.idDeporte && deportes.value.length > 0) {
      filtros.value.idDeporte = deportes.value[0].idDeporte;
    }
  } catch (err) {
    console.error("Error cargando deportes", err);
  }
};

const cargarTiposCliente = async () => {
  try {
    const data = await apiGetTipoCliente({ signal: controller?.signal });
    const lista = Array.isArray(data)
      ? data
      : data?.results ?? data?.items ?? [];

    tipos_cliente.value = lista.map((s, i) => ({
      id: s.id_tipo_cliente,
      descripcion: s.descripcion ?? "",
      alta_fecha: s.alta_fecha,
      alta_usuario: s.alta_usuario,
    }));
  } catch (err) {
    console.error("Error cargando tipos de cliente", err);
  }
};

const cargarCanchas = async () => {
  if (!filtros.value.idDeporte) {
    canchas.value = [];
    return;
  }

  try {
    const data = await apiGetCanchasPorDeporte(
      { idDeporte: filtros.value.idDeporte },
      {
        signal: controller?.signal,
      },
    );
    const lista = Array.isArray(data)
      ? data
      : data?.results ?? data?.items ?? [];
    canchas.value = lista.map((s, i) => ({
      idCancha: s.id_cancha ?? i,
      idDeporte: s.id_deporte,
      descripcion: s.descripcion ?? "",
    }));
  } catch (err) {
    console.error("Error cargando canchas", err);
  }
};

const cargarRangoHorario = async () => {
  if (!filtros.value.idDeporte || !filtros.value.fecha) return;

  try {
    const params = {
      fecha: filtros.value.fecha, // 👈 fecha del filtro
      id_deporte: filtros.value.idDeporte,
      id_cancha: filtros.value.idCancha || undefined,
    };

    const data = await apiGetRangoHorario(params, {
      signal: controller?.signal,
    });

    if (data?.hora_min && data?.hora_max) {
      const desde = String(data.hora_min).slice(0, 5); // "HH:MM"
      const hasta = String(data.hora_max).slice(0, 5);

      configTurnos.value.horaDesde = desde;
      configTurnos.value.horaHasta = hasta;
    }
  } catch (err) {
    console.error("Error cargando rango horario", err);
  }
};

const cargarFormasPago = async () => {
  try {
    const data = await apiGetFormasPago({ signal: controller?.signal });
    const lista = Array.isArray(data)
      ? data
      : data?.results ?? data?.items ?? [];

    formasPagoConfig.value = lista.map((x) => ({
      idFormaPago: x.id_forma_pago,
      descripcion: x.descripcion,
      activo: x.activo ?? 1,
    }));
  } catch (e) {
    console.error("Error cargando formas de pago", e);
  }
};

const cargarConfigFormaPagoVigente = async (fecha) => {
  if (!fecha) return;

  try {
    const data = await apiGetFormaPagoConfigVigente(
      { fecha },
      { signal: controller?.signal },
    );

    const lista = Array.isArray(data) ? data : [];

    formasPagoConfig.value = lista.map((x) => ({
      fechaDesde: x.fecha_desde,
      idFormaPago: x.id_forma_pago,
      descripcion: x.descripcion,
      esDefault: x.es_default === 1,
      importeAjuste: Number(x.importe_ajuste ?? 0),
    }));

    if (!formReserva.value.idFormaPago) {
      const def = formasPagoConfig.value.find((x) => x.esDefault);
      formReserva.value.idFormaPago = def?.idFormaPago ?? null;
    }
  } catch (e) {
    console.error("Error cargando configuración de formas de pago", e);
    formasPagoConfig.value = [];
  }
};

const formaPagoSeleccionadaConfig = computed(() => {
  return (
    formasPagoConfig.value.find(
      (x) => x.idFormaPago === formReserva.value.idFormaPago,
    ) ?? null
  );
});

const cargarReservas = async () => {
  try {
    const data = await apiGetReservas(
      {
        fecha: filtros.value.fecha,
        id_deporte: filtros.value.idDeporte,
        id_cancha: filtros.value.idCancha,
      },
      {
        signal: controller?.signal,
      },
    );
    reservas.value = (Array.isArray(data) ? data : []).map((r) => {
      // Backend manda datetime ISO, sacamos solo la hora y la fecha
      const fechaStr = (r.fecha || "").slice(0, 10);
      const horaDesde = normalizarHoraValidacion(r.hora_desde);
      const horaHasta = normalizarHoraValidacion(r.hora_hasta);

      const minDesde = toMinutes(horaDesde);
      const minHasta = toMinutes(horaHasta);
      const duracion =
        minDesde != null && minHasta != null && minHasta > minDesde
          ? minHasta - minDesde
          : configTurnos.value.duracionMin;

      return {
        idReserva: r.id_reserva,
        idCancha: r.id_cancha,
        fecha: fechaStr,
        hora: horaDesde,
        duracionMin: duracion,

        cliente: r.cliente?.nombre ?? "",
        clienteTelefono: r.cliente?.celular ?? "",
        clienteEmail: usaEmailCliente ? r.cliente?.email ?? "" : "",
        idCliente: r.cliente?.id_cliente ?? null,
        tipoCliente: r.cliente?.tipo_cliente ?? null,

        observaciones: r.observaciones ?? "",

        pagado: r.pagado ?? 0,
        monto_a_pagar: r.monto_a_pagar ?? null,
        monto_pagado: Number(r.monto_pagado ?? 0),

        idFormaPago: r.id_forma_pago ?? null,
        formaPagoDesc: r.forma_pago_desc ?? "",
      };
    });
  } catch (err) {
    console.error("Error cargando reservas", err);
  }
};

const buscarCliente = async () => {
  // Si no hay nada cargado en nombre/celular/email, no buscamos
  if (
    !formReserva.value.clienteNombre &&
    !formReserva.value.clienteTelefono &&
    (!usaEmailCliente || !formReserva.value.clienteEmail)
  ) {
    limpiarSugerenciasCliente();
    return;
  }

  try {
    const params = {
      nombre: formReserva.value.clienteNombre || undefined,
      celular: formReserva.value.clienteTelefono || undefined,
      email: usaEmailCliente ? formReserva.value.clienteEmail || undefined : undefined,
      skip: 0,
      limit: 20,
    };

    const data = await apiBuscarCliente(params, {
      signal: controller?.signal,
    });

    const lista = Array.isArray(data) ? data : [];

    resultadosCliente.value = lista;
    mostrandoSugerenciasCliente.value = lista.length > 0;
    indiceClienteActivo.value = lista.length > 0 ? 0 : -1;
  } catch (err) {
    console.error("Error buscando clientes", err);
    limpiarSugerenciasCliente();
  }
};
/* Fin Carga de datos */

/* Inicio watchs */
watch([horaDesdeHora, horaDesdeMin], () => {
  const hh = String(horaDesdeHora.value).padStart(2, "0");
  const mm = String(horaDesdeMin.value).padStart(2, "0");
  formReserva.value.horaDesde = `${hh}:${mm}`;
});

watch([horaHastaHora, horaHastaMin], () => {
  const hh = String(horaHastaHora.value).padStart(2, "0");
  const mm = String(horaHastaMin.value).padStart(2, "0");
  formReserva.value.horaHasta = `${hh}:${mm}`;
});

watch(
  () => filtros.value.idDeporte,
  async (nuevo) => {
    router.replace({
      query: {
        ...route.query,
        idDeporte: nuevo ?? undefined, // si es null lo saca de la URL
      },
    });

    if (!nuevo) {
      filtros.value.idCancha = null;
      canchas.value = [];
      return;
    }

    await cargarCanchas();
    await cargarRangoHorario();
    await cargarReservas();

    const existe = canchas.value.some(
      (c) => c.idCancha === filtros.value.idCancha,
    );

    if (!existe) {
      filtros.value.idCancha = null;
    }
  },
  { immediate: true },
);

watch(
  () => ({
    fecha: filtros.value.fecha,
    idDeporte: filtros.value.idDeporte,
    idCancha: filtros.value.idCancha,
  }),
  () => {
    if (filtros.value.idDeporte) {
      cargarReservas();
    }
  },
  { deep: true, immediate: true },
);

watch(
  () => filtros.value.idCancha,
  () => {
    if (filtros.value.idDeporte) {
      cargarRangoHorario();
      cargarReservas();
    }
  },
);

watch(
  () => filtros.value.fecha,
  () => {
    if (filtros.value.idDeporte) {
      cargarRangoHorario();
      cargarReservas();
    }
  },
);
watch(
  () => formReserva.value.idDeporte,
  () => {
    if (
      formReserva.value.idCancha &&
      !canchasModal.value.some((c) => c.idCancha === formReserva.value.idCancha)
    ) {
      formReserva.value.idCancha = null;
    }
  },
);

watch(
  () => formReserva.value.fecha,
  async (fecha) => {
    if (modalNuevaAbierta.value && fecha) {
      await cargarConfigFormaPagoVigente(fecha);
    }
  },
);

/* Fin watchs */

// 🔎 Autosuggest de clientes
const resultadosCliente = ref([]);
const mostrandoSugerenciasCliente = ref(false);
const indiceClienteActivo = ref(-1);
let buscarClienteTimeout = null;

const limpiarSugerenciasCliente = () => {
  resultadosCliente.value = [];
  mostrandoSugerenciasCliente.value = false;
  indiceClienteActivo.value = -1;
};

const dispararBusquedaCliente = () => {
  if (buscarClienteTimeout) {
    clearTimeout(buscarClienteTimeout);
  }
  // pequeño debounce
  buscarClienteTimeout = setTimeout(() => {
    buscarCliente();
  }, 300);
};

const seleccionarCliente = (cliente) => {
  if (!cliente) return;

  formReserva.value.idCliente = cliente.id_cliente;
  formReserva.value.clienteNombre = cliente.nombre || "";
  formReserva.value.clienteTelefono = cliente.celular || "";
  formReserva.value.clienteEmail = usaEmailCliente ? cliente.email || "" : "";
  formReserva.value.tipoCliente =
    cliente.tipo_cliente ?? formReserva.value.tipoCliente;
  formReserva.value.enviarAvisoEmail = usaEmailCliente ? !!cliente.aviso_email : false;

  limpiarSugerenciasCliente();
};

const manejarKeydownCliente = (event) => {
  if (!mostrandoSugerenciasCliente.value || !resultadosCliente.value.length) {
    return;
  }

  const maxIndex = resultadosCliente.value.length - 1;

  if (event.key === "ArrowDown") {
    event.preventDefault();
    indiceClienteActivo.value =
      indiceClienteActivo.value < maxIndex ? indiceClienteActivo.value + 1 : 0;
  } else if (event.key === "ArrowUp") {
    event.preventDefault();
    indiceClienteActivo.value =
      indiceClienteActivo.value > 0 ? indiceClienteActivo.value - 1 : maxIndex;
  } else if (event.key === "Enter") {
    event.preventDefault();
    const cliente = resultadosCliente.value[indiceClienteActivo.value];
    if (cliente) {
      seleccionarCliente(cliente);
    }
  } else if (event.key === "Escape") {
    event.preventDefault();
    limpiarSugerenciasCliente();
  }
};

const abrirModalNuevaReserva = async () => {
  resetFormReserva();
  esBajaReserva.value = false;
  canchaValidadaPendiente.value = null;
  precioValidadoPendiente.value = null;

  formReserva.value.idDeporte = filtros.value.idDeporte;
  formReserva.value.idCancha = filtros.value.idCancha ?? null;
  formReserva.value.fecha = filtros.value.fecha;

  await cargarConfigFormaPagoVigente(formReserva.value.fecha);
  modalNuevaAbierta.value = true;
};

const sumarMinutosHora = (hora, minutos) => {
  const base = toMinutes(hora);
  if (base == null) return hora;

  const total = base + minutos;
  const hh = String(Math.floor(total / 60)).padStart(2, "0");
  const mm = String(total % 60).padStart(2, "0");
  return `${hh}:${mm}`;
};

/**
 * Indica si un bloque horario se superpone con una reserva existente.
 * Ej.: si existe 16:40-17:40, el bloque 16:30-17:00 se considera ocupado.
 */
const bloqueHorarioOcupado = (idCancha, horaDesde, duracionMin = 30) => {
  const minDesde = toMinutes(horaDesde);
  if (minDesde == null) return false;

  const minHasta = minDesde + duracionMin;

  return reservas.value.some((r) => {
    if (!r || r.idCancha !== idCancha || r.fecha !== filtros.value.fecha) {
      return false;
    }

    const minInicioReserva = toMinutes(r.horaInicio || r.hora);
    if (minInicioReserva == null) return false;

    const duracionReserva = r.duracionMin ?? configTurnos.value.duracionMin;
    const minFinReserva = minInicioReserva + duracionReserva;

    return minDesde < minFinReserva && minHasta > minInicioReserva;
  });
};

const abrirModalNuevaReservaDesdeGrilla = async (cancha, slot) => {
  if (!cancha?.idCancha || !slot) return;

  resetFormReserva();
  esBajaReserva.value = false;
  canchaValidadaPendiente.value = null;
  precioValidadoPendiente.value = null;

  formReserva.value.idDeporte = filtros.value.idDeporte;
  formReserva.value.idCancha = cancha.idCancha;
  formReserva.value.fecha = filtros.value.fecha;

  const minDesde = toMinutes(slot);
  const minHasta = minDesde + configTurnos.value.duracionMin;

  const horaDesde = `${String(Math.floor(minDesde / 60)).padStart(
    2,
    "0",
  )}:${String(minDesde % 60).padStart(2, "0")}`;

  const horaHasta = `${String(Math.floor(minHasta / 60)).padStart(
    2,
    "0",
  )}:${String(minHasta % 60).padStart(2, "0")}`;

  formReserva.value.horaDesde = horaDesde;
  formReserva.value.horaHasta = horaHasta;

  horaDesdeHora.value = Math.floor(minDesde / 60);
  horaDesdeMin.value = minDesde % 60;

  horaHastaHora.value = Math.floor(minHasta / 60);
  horaHastaMin.value = minHasta % 60;

  await cargarConfigFormaPagoVigente(formReserva.value.fecha);

  modalNuevaAbierta.value = true;
};

const formatearFechaWhatsApp = (fecha) => {
  if (!fecha) return "";
  const [yyyy, mm, dd] = String(fecha).slice(0, 10).split("-");
  return yyyy && mm && dd ? `${dd}/${mm}/${yyyy}` : fecha;
};

const dineroWhatsApp = (valor) => {
  const n = Number(valor ?? 0);
  return formatearPrecio(Number.isFinite(n) ? n : 0);
};

const reemplazarVariablesWhatsApp = (texto) => {
  const deporte = deportes.value.find(
    (d) => Number(d.idDeporte) === Number(formReserva.value.idDeporte),
  );
  const cancha = canchas.value.find(
    (c) => Number(c.idCancha) === Number(formReserva.value.idCancha),
  );

  const montoAPagar = Number(pagoMontoAPagar.value ?? 0);
  const montoPagado = formReserva.value.idReserva
    ? Number(pagoMontoPagado.value ?? 0)
    : 0;
  const saldoPendiente = Math.max(0, montoAPagar - montoPagado);

  const vars = {
    "{cliente}": formReserva.value.clienteNombre || "",
    "{deporte}": deporte?.descripcion || "",
    "{cancha}": cancha?.descripcion || "",
    "{fecha}": formatearFechaWhatsApp(formReserva.value.fecha),
    "{hora_desde}": formReserva.value.horaDesde || "",
    "{hora_hasta}": formReserva.value.horaHasta || "",
    "{precio}": dineroWhatsApp(montoAPagar),
    "{monto_pagado}": dineroWhatsApp(montoPagado),
    "{saldo_pendiente}": dineroWhatsApp(saldoPendiente),
  };

  return Object.entries(vars).reduce(
    (salida, [variable, valor]) => salida.split(variable).join(valor),
    texto || "",
  );
};

const abrirSelectorWhatsApp = async () => {
  cargandoMensajesWhatsApp.value = true;
  mensajesWhatsApp.value = [];
  modalWhatsAppPlantillas.value = true;

  try {
    const data = await apiGetMensajesWhatsAppActivos({
      signal: controller?.signal,
    });
    mensajesWhatsApp.value = Array.isArray(data) ? data : [];
  } catch (e) {
    console.error("Error cargando mensajes de WhatsApp", e);
    modalWhatsAppPlantillas.value = false;
    mensajeError.value = "No se pudieron cargar las plantillas de WhatsApp.";
    mostrarMensajeError.value = true;
  } finally {
    cargandoMensajesWhatsApp.value = false;
  }
};

const seleccionarMensajeWhatsApp = (plantilla) => {
  mensajeWhatsAppPreview.value = reemplazarVariablesWhatsApp(
    plantilla?.mensaje,
  );
  telefonoWhatsApp.value = formReserva.value.clienteTelefono || "";
  modalWhatsAppPlantillas.value = false;
  modalWhatsAppPreview.value = true;
};

const volverAPlantillasWhatsApp = () => {
  modalWhatsAppPreview.value = false;
  modalWhatsAppPlantillas.value = true;
};

const normalizarTelefonoWhatsApp = (telefono) => {
  let digits = String(telefono || "").replace(/\D/g, "");
  if (digits.startsWith("0")) digits = digits.slice(1);
  // Para celulares argentinos cargados como 11XXXXXXXX, 221XXXXXXX, etc.
  if (digits.length === 10 && !digits.startsWith("54")) digits = `549${digits}`;
  return digits;
};

const abrirWhatsApp = () => {
  const telefono = normalizarTelefonoWhatsApp(telefonoWhatsApp.value);
  if (!telefono) {
    mensajeError.value =
      "La reserva no tiene un teléfono válido para WhatsApp.";
    mostrarMensajeError.value = true;
    return;
  }
  if (!mensajeWhatsAppPreview.value.trim()) {
    mensajeError.value = "El mensaje de WhatsApp está vacío.";
    mostrarMensajeError.value = true;
    return;
  }

  const url = `https://wa.me/${telefono}?text=${encodeURIComponent(
    mensajeWhatsAppPreview.value,
  )}`;
  window.open(url, "_blank", "noopener,noreferrer");
};

const onCancelModalReserva = () => {
  modalNuevaAbierta.value = false;
};

const onCancelModalConfirm = () => {
  mostrarMensajeConfirmacion.value = false;
  canchaValidadaPendiente.value = null;
  precioValidadoPendiente.value = null;
};

function validarDatosReserva() {
  if (!formReserva.value.idDeporte) {
    mensajeError.value = "Debe seleccionar el deporte";
    mostrarMensajeError.value = true;
    duracionMensajeError.value = 5000;
    return false;
  }

  if (
    formReserva.value.idCancha &&
    !canchasModal.value.some((c) => c.idCancha === formReserva.value.idCancha)
  ) {
    mensajeError.value =
      "La cancha seleccionada no corresponde al deporte elegido";
    mostrarMensajeError.value = true;
    duracionMensajeError.value = 5000;
    return false;
  }

  if (!formReserva.value.fecha) {
    mensajeError.value = "Debe ingresar la fecha de la reserva";
    mostrarMensajeError.value = true;
    duracionMensajeError.value = 5000;
    return false;
  }

  const fechaReservaDate = new Date(formReserva.value.fecha);
  const hoyDate = new Date(hoy);

  if (fechaReservaDate < hoyDate) {
    mensajeError.value = "La fecha de la reserva no puede ser anterior a hoy.";
    mostrarMensajeError.value = true;
    duracionMensajeError.value = 5000;
    return false;
  }

  if (fechaMaximaModificacion.value) {
    const maxDate = fechaMaximaModificacion.value;
    if (fechaReservaDate > maxDate) {
      const maxStr = maxDate
        .toISOString()
        .slice(0, 10)
        .split("-")
        .reverse()
        .join("/");
      mensajeError.value = `La fecha de la reserva no puede ser posterior al ${maxStr}.`;
      mostrarMensajeError.value = true;
      duracionMensajeError.value = 5000;
      return false;
    }
  }

  if (!formReserva.value.horaDesde || !formReserva.value.horaHasta) {
    mensajeError.value = "Completá hora desde y hora hasta";
    mostrarMensajeError.value = true;
    duracionMensajeError.value = 5000;
    return false;
  }

  const minDesde = toMinutes(formReserva.value.horaDesde);
  const minHasta = toMinutes(formReserva.value.horaHasta);
  const duracion = minHasta - minDesde;

  if (minDesde == null || minHasta == null || duracion <= 0) {
    mensajeError.value = "El rango horario no es válido";
    mostrarMensajeError.value = true;
    duracionMensajeError.value = 5000;
    return false;
  }

  if (!formReserva.value.clienteNombre || !formReserva.value.clienteTelefono) {
    mensajeError.value = "Ingresar el nombre y teléfono del cliente";
    mostrarMensajeError.value = true;
    duracionMensajeError.value = 5000;
    return false;
  }

  if (!formReserva.value.tipoCliente) {
    mensajeError.value = "Ingresar el tipo de cliente";
    mostrarMensajeError.value = true;
    duracionMensajeError.value = 5000;
    return false;
  }

  guardandoReserva.value = true;
  return true;
}

function onEliminarReserva() {
  esBajaReserva.value = true;

  mensajeConfirmacion.value = "Confirma que desea eliminar la reserva?";
  mostrarMensajeConfirmacion.value = true;
}

const onValidarReserva = async () => {
  esBajaReserva.value = false;
  canchaValidadaPendiente.value = null;
  precioValidadoPendiente.value = null;
  if (!validarDatosReserva()) {
    return;
  }

  const payloadValidar = {
    fecha: formReserva.value.fecha,
    id_deporte: formReserva.value.idDeporte,
    hora_desde: formReserva.value.horaDesde,
    hora_hasta: formReserva.value.horaHasta,
    id_tipo_cliente: formReserva.value.tipoCliente,
    id_cancha: formReserva.value.idCancha ?? null,
    id_reserva: formReserva.value.idReserva ?? 0,
    id_forma_pago: formReserva.value.idFormaPago,
  };

  try {
    guardandoReserva.value = true;

    const resp = await apiGetValidarReserva(payloadValidar, {
      signal: controller?.signal,
    });

    if (!resp) {
      mensajeError.value = "No se recibió respuesta del servidor";
      mostrarMensajeError.value = true;
      duracionMensajeError.value = 5000;
      return;
    }

    if (!resp.disponible) {
      mensajeError.value =
        resp.mensaje || "No hay canchas disponibles en ese horario.";
      mostrarMensajeError.value = true;
      duracionMensajeError.value = 0;
      return;
    } else {
      const idCanchaSolicitada = payloadValidar.id_cancha
        ? Number(payloadValidar.id_cancha)
        : null;
      const idCanchaSugerida = resp.id_cancha ? Number(resp.id_cancha) : null;
      const huboCambioDeCancha =
        idCanchaSolicitada &&
        idCanchaSugerida &&
        idCanchaSolicitada !== idCanchaSugerida;

      canchaValidadaPendiente.value = idCanchaSugerida;
      precioValidadoPendiente.value = resp.precio;

      const advertenciaHorario = await obtenerAdvertenciaHorarioConfigurado({
        idCancha: idCanchaSugerida,
        fecha: payloadValidar.fecha,
        horaDesde: payloadValidar.hora_desde,
        horaHasta: payloadValidar.hora_hasta,
      });

      if (huboCambioDeCancha) {
        mensajeConfirmacion.value =
          "La cancha " +
          getDescripcionCancha(idCanchaSolicitada) +
          " no está disponible para ese horario. " +
          "El sistema sugiere " +
          (resp.desc_cancha || getDescripcionCancha(idCanchaSugerida)) +
          ". ¿Querés continuar con la reserva para " +
          deporteSeleccionado.value.descripcion +
          " de " +
          payloadValidar.hora_desde +
          " a " +
          payloadValidar.hora_hasta +
          "?" +
          advertenciaHorario;
      } else {
        mensajeConfirmacion.value =
          "Confirma realizar la reserva para " +
          deporteSeleccionado.value.descripcion +
          " de " +
          payloadValidar.hora_desde +
          " a " +
          payloadValidar.hora_hasta +
          " para la cancha " +
          (resp.desc_cancha || getDescripcionCancha(idCanchaSugerida)) +
          "?" +
          advertenciaHorario;
      }

      mostrarMensajeConfirmacion.value = true;
    }
  } catch (e) {
    console.error("Error validando reserva en backend", e);
    mensajeError.value =
      "Error validando reserva. Contacte al administrador del sistema por favor";
    mostrarMensajeError.value = true;
    duracionMensajeError.value = 5000;
  } finally {
    guardandoReserva.value = false;
  }
};

const onConfirmReserva = async () => {
  if (!validarDatosReserva()) {
    return;
  }

  // Si el backend sugirió una cancha, la asignamos primero
  if (canchaValidadaPendiente.value) {
    formReserva.value.idCancha = Number(canchaValidadaPendiente.value);
  }

  // Recién después validamos que haya una cancha asignada
  if (!formReserva.value.idCancha) {
    mensajeError.value =
      "No se pudo asignar una cancha disponible. Intente nuevamente.";
    mostrarMensajeError.value = true;
    duracionMensajeError.value = 5000;
    guardandoReserva.value = false;
    return false;
  }

  if (precioValidadoPendiente.value !== null) {
    precio.value = precioValidadoPendiente.value;
  }

  try {
    const idCanchaDestino = formReserva.value.idCancha;

    // Fecha/hora en ISO para enviar al backend
    const horaDesdeISO = buildDateTimeLocal(
      formReserva.value.fecha,
      formReserva.value.horaDesde,
    );

    const horaHastaISO = buildDateTimeLocal(
      formReserva.value.fecha,
      formReserva.value.horaHasta,
    );

    const reservaEnviar = {
      id_cancha: idCanchaDestino,
      fecha: formReserva.value.fecha,
      hora_desde: formReserva.value.horaDesde,
      hora_hasta: formReserva.value.horaHasta,
      pagado: 0,
      es_abono: formReserva.value.generarAbono ? 1 : 0,
      id_reserva: formReserva.value.idReserva ?? 0,
      observaciones: formReserva.value.observaciones ?? null,
      baja_fecha: esBajaReserva.value ? new Date().toISOString() : null,
      cliente: {
        nombre: formReserva.value.clienteNombre,
        celular: formReserva.value.clienteTelefono,
        email: usaEmailCliente ? formReserva.value.clienteEmail || null : null,
        tipo_cliente: formReserva.value.tipoCliente,
        id_cliente: formReserva.value.idCliente ?? 0,
      },
    };


    let resp;

    try {
      if (
        formReserva.value.idReserva == 0 ||
        formReserva.value.idReserva == null
      ) {
        resp = await apiPostGrabarReserva(reservaEnviar, {
          signal: controller?.signal,
        });
      } else {
        resp = await apiPutReserva(formReserva.value.idReserva, reservaEnviar, {
          signal: controller?.signal,
        });
      }

      if (!resp) {
        mensajeError.value = "No se recibió respuesta del servidor";
        mostrarMensajeError.value = true;
        duracionMensajeError.value = 5000;
        return;
      }

      if (resp.error) {
        mensajeError.value =
          "Error al grabar la reserva: " + resp.error ||
          "Ocurrió un error al grabar la reserva";
        mostrarMensajeError.value = true;
        duracionMensajeError.value = 5000;
        return;
      }

      await cargarReservas();

      modalNuevaAbierta.value = false;
      canchaValidadaPendiente.value = null;
      precioValidadoPendiente.value = null;

      if (esBajaReserva.value) {
        mensajeOk.value = "La reserva se eliminó correctamente";
      } else if (estaEditando.value) {
        mensajeOk.value = "La reserva se actualizó correctamente";
      } else {
        mensajeOk.value = "La reserva se grabó correctamente";
      }

      esBajaReserva.value = false;

      mostrarMensajeOk.value = true;
      mostrarMensajeError.value = false;
    } catch (e) {
      console.error("Error grabando reserva en backend", e);

      mensajeError.value =
        "Error grabando reserva. Contacte al administrador del sistema por favor";

      mostrarMensajeError.value = true;
      duracionMensajeError.value = 0;
    }
  } finally {
    guardandoReserva.value = false;
  }
};

const onChangePagadoReserva = async () => {
  if (!pagoPagado.value) {
    formReserva.value.idFormaPago = null;
    pagoMontoPagado.value = null;
    return;
  }

  await cargarConfigFormaPagoVigente(formReserva.value.fecha);

  if (!formReserva.value.idFormaPago) {
    const def = formasPagoConfig.value.find((x) => x.esDefault);
    formReserva.value.idFormaPago = def?.idFormaPago ?? null;
  }

  if (pagoMontoPagado.value == null || pagoMontoPagado.value === "") {
    pagoMontoPagado.value = pagoMontoAPagar.value ?? 0;
  }

  await onChangeFormaPagoReserva();
};

const onChangeFormaPagoReserva = async () => {
  if (!pagoPagado.value || !formReserva.value.idFormaPago) return;

  try {
    const payloadValidar = {
      fecha: formReserva.value.fecha,
      id_deporte: formReserva.value.idDeporte,
      hora_desde: formReserva.value.horaDesde,
      hora_hasta: formReserva.value.horaHasta,
      id_tipo_cliente: formReserva.value.tipoCliente,
      id_cancha: formReserva.value.idCancha ?? null,
      id_reserva: formReserva.value.idReserva ?? 0,
      id_forma_pago: formReserva.value.idFormaPago,
    };

    const resp = await apiGetValidarReserva(payloadValidar, {
      signal: controller?.signal,
    });

    if (resp?.precio != null) {
      pagoMontoAPagar.value = Number(resp.precio);
      pagoMontoPagado.value = Number(resp.precio);
      return;
    }

    // Fallback por si el backend no devuelve precio
    const ajuste = formaPagoSeleccionadaConfig.value?.importeAjuste ?? 0;
    pagoMontoPagado.value = Number(pagoMontoAPagar.value ?? 0) + Number(ajuste);
  } catch (e) {
    console.error("Error recalculando monto por forma de pago", e);

    // Fallback local si falla la validación
    const ajuste = formaPagoSeleccionadaConfig.value?.importeAjuste ?? 0;
    pagoMontoPagado.value = Number(pagoMontoAPagar.value ?? 0) + Number(ajuste);
  }
};

const guardarPagoReserva = async () => {
  if (!estaEditando.value || !formReserva.value.idReserva) return;

  try {
    guardandoPago.value = true;

    const payload = {
      pagado: pagoPagado.value ? 1 : 0,
      monto_pagado: pagoPagado.value ? pagoMontoPagado.value ?? 0 : null,
      monto_a_pagar: pagoMontoAPagar.value ?? null,
      id_forma_pago: pagoPagado.value ? formReserva.value.idFormaPago : null,
    };

    await apiPutReservaPagada(formReserva.value.idReserva, payload, {
      signal: controller?.signal,
    });

    await cargarReservas();

    mensajeOk.value = "Pago actualizado correctamente";
    mostrarMensajeOk.value = true;
    mostrarMensajeError.value = false;
    modalNuevaAbierta.value = false;
  } catch (e) {
    console.error("Error guardando pago", e);
    mensajeError.value = "No se pudo guardar el pago.";
    mostrarMensajeError.value = true;
    duracionMensajeError.value = 5000;
  } finally {
    guardandoPago.value = false;
  }
};

/**
 * 🔹 Hooks para editar reserva (queda igual que antes)
 */
const abrirModalEditarReserva = async (reserva) => {
  if (!reserva) return;

  // Primero limpio todo
  resetFormReserva();

  // Datos principales
  formReserva.value.idReserva = reserva.idReserva ?? null;
  formReserva.value.idDeporte = filtros.value.idDeporte;
  formReserva.value.idCancha = reserva.idCancha ?? null;
  formReserva.value.fecha = reserva.fecha ?? filtros.value.fecha;

  // Ahora sí cargo la configuración según la fecha de la reserva
  await cargarConfigFormaPagoVigente(formReserva.value.fecha);

  // Y después seteo la forma de pago guardada en la reserva
  formReserva.value.idFormaPago =
    reserva.idFormaPago != null ? Number(reserva.idFormaPago) : null;

  // Horarios
  formReserva.value.horaDesde = reserva.horaInicio;
  formReserva.value.horaHasta = reserva.horaFin;

  const [hdH, hdM] = formReserva.value.horaDesde.split(":").map(Number);
  const [hhH, hhM] = formReserva.value.horaHasta.split(":").map(Number);

  horaDesdeHora.value = hdH;
  horaDesdeMin.value = hdM;
  horaHastaHora.value = hhH;
  horaHastaMin.value = hhM;

  // Datos de cliente
  formReserva.value.clienteNombre = reserva.cliente || "";
  formReserva.value.clienteTelefono = reserva.clienteTelefono || "";
  formReserva.value.clienteEmail = usaEmailCliente ? reserva.clienteEmail || "" : "";
  formReserva.value.idCliente = reserva.idCliente ?? null;
  formReserva.value.tipoCliente =
    reserva.tipoCliente ?? formReserva.value.tipoCliente;

  // Observaciones
  formReserva.value.observaciones = reserva.observaciones ?? "";

  // Pago
  pagoPagado.value = (reserva.pagado ?? 0) === 1;
  pagoMontoAPagar.value = reserva.monto_a_pagar ?? null;
  pagoMontoPagado.value = reserva.monto_pagado ?? null;

  if (
    pagoPagado.value &&
    (pagoMontoPagado.value == null || pagoMontoPagado.value === "")
  ) {
    pagoMontoPagado.value = pagoMontoAPagar.value ?? null;
  }

  formReserva.value.generarAbono = false;

  modalNuevaAbierta.value = true;
};

const resetFormReserva = () => {
  formReserva.value = {
    idDeporte: filtros.value.idDeporte ?? null,
    idCancha: filtros.value.idCancha ?? null,
    fecha: filtros.value.fecha ?? hoy,
    horaDesde: "10:00",
    horaHasta: "11:00",
    generarAbono: false,
    clienteNombre: "",
    clienteTelefono: "",
    clienteEmail: "",
    enviarAvisoEmail: false,
    tipoCliente: 1,
    observaciones: "",
    idCliente: null,
    idReserva: null,
  };

  // sincronizar selects de hora con las cadenas
  const [hdH, hdM] = formReserva.value.horaDesde.split(":").map(Number);
  const [hhH, hhM] = formReserva.value.horaHasta.split(":").map(Number);

  horaDesdeHora.value = hdH;
  horaDesdeMin.value = hdM;
  horaHastaHora.value = hhH;
  horaHastaMin.value = hhM;

  pagoPagado.value = false;
  pagoMontoAPagar.value = null;
  pagoMontoPagado.value = null;
  guardandoPago.value = false;

  // limpiar autosuggest de clientes
  limpiarSugerenciasCliente();
};

const onInputMontoPago = (e) => {
  const raw = e?.target?.value;
  const num = raw === "" ? null : Number(raw);

  if (pagoPagado.value) {
    pagoMontoPagado.value = Number.isNaN(num) ? null : num;
  } else {
    pagoMontoAPagar.value = Number.isNaN(num) ? null : num;
  }
};

function cambiarFechaReserva(cantidadDias) {
  if (!filtros.value.fecha) return;

  const [year, month, day] = filtros.value.fecha.split("-").map(Number);

  const fecha = new Date(year, month - 1, day);
  fecha.setDate(fecha.getDate() + cantidadDias);

  const yyyy = fecha.getFullYear();
  const mm = String(fecha.getMonth() + 1).padStart(2, "0");
  const dd = String(fecha.getDate()).padStart(2, "0");

  filtros.value.fecha = `${yyyy}-${mm}-${dd}`;

  cargarReservas();
}
</script>

<style scoped>
.reservas-grid-wrapper {
  max-width: 100%;
}

.reservas-grid-scroll {
  overflow-x: auto;
}

.reservas-grid {
  border-collapse: separate;
  border-spacing: 0;
}

.reservas-grid th,
.reservas-grid td {
  white-space: nowrap;
  padding: 0.35rem 0.5rem;
}

.reservas-cell {
  height: 42px;
  vertical-align: middle;
  padding: 0;
  position: relative;
}

.reserva-block {
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  background: rgba(25, 135, 84, 0.15);
  border: 1px solid rgba(25, 135, 84, 0.6);
  font-size: 0.65rem;
  cursor: pointer;

  display: flex;
  flex-direction: column;
  justify-content: flex-start; /* nombre arriba */
  align-items: stretch;

  padding: 2px 4px;
  border-radius: 0;
  border-top-width: 0;
  border-bottom-width: 0;
  z-index: 1;

  white-space: normal;
  overflow: hidden;
}

.reserva-block:hover {
  background: rgba(25, 135, 84, 0.25);
}

.reserva-block--start {
  border-top-width: 1px;
  border-top-left-radius: 6px;
  border-top-right-radius: 6px;
}

.reserva-block--end {
  border-bottom-width: 1px;
  border-bottom-left-radius: 6px;
  border-bottom-right-radius: 6px;
}

.reserva-block--single {
  border-radius: 6px;
  border-top-width: 1px;
  border-bottom-width: 1px;
}

.reserva-titulo {
  font-weight: 600;
  line-height: 1.1;
  text-align: center;
  max-width: 100%;
  word-break: break-word;
}

.reserva-detalle {
  font-size: 0.7rem;
  line-height: 1.1;
  opacity: 0.8;
  text-align: center;

  margin-top: auto;
  padding-bottom: 1px;
}

.reserva-empty {
  width: 100%;
  height: 42px;
  background: #f8f9fa;
  border-radius: 4px;
  overflow: hidden;
  position: relative;
  z-index: 0;
}

/* En vista de 60 min, cada bloque se parte en :00 y :30 */
.reserva-empty--split {
  display: flex;
  flex-direction: column;
}

.reserva-empty-half,
.reserva-empty-full {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  border: 0;
  padding: 0;
  margin: 0;
  background: transparent;
  cursor: pointer;
  color: #6c757d;
  font-size: 0.6rem;
  line-height: 1;
  transition: background-color 0.15s ease, color 0.15s ease;
}

.reserva-empty-half {
  height: 21px;
  flex: 0 0 21px;
}

.reserva-empty-full {
  height: 42px;
}

.reserva-empty-half--ocupado {
  cursor: default;
  pointer-events: none;
}

/* Línea siempre visible entre :00 y :30 */
.reserva-empty-half + .reserva-empty-half {
  border-top: 1px solid #adb5bd;
}

.reserva-empty-half:hover,
.reserva-empty-full:hover {
  background: #e6f4ea;
  color: #146c43;
}

/* La hora queda visible para que se note claramente que hay dos opciones */
.reserva-empty-half span,
.reserva-empty-full span {
  opacity: 0.72;
  pointer-events: none;
}

.reserva-empty-half:hover span,
.reserva-empty-full:hover span {
  opacity: 1;
  font-weight: 600;
}

.wa-template-preview {
  white-space: normal;
  max-height: none;
  overflow: visible;
  color: #6c757d;
}

.btn-outline-success:hover .wa-template-preview {
  color: #fff !important;
}

.wa-mensaje-editable {
  resize: vertical;
}

.sticky-col {
  /*
  position: sticky;
  left: 0;
  background: #fff;
  z-index: 2;
  */
  width: 1%;
  min-width: 70px;
  max-width: 70px;
  white-space: nowrap;
}

.duracion-turnos .btn:focus {
  box-shadow: none !important;
  outline: none !important;
}

/* Panel derecho para el BaseModal */
.fo-panel-right {
  margin-left: auto;
  width: 420px;
  max-width: 100%;
}

/* Cuando una celda es continuación de la misma reserva, escondemos esa línea */
.reservas-cell--continuacion {
  border-top-color: transparent !important;
}

.lista-sugerencias-cliente {
  position: absolute;
  z-index: 10;
  top: 100%;
  left: 0;
  right: 0;
  margin-top: 2px;
  padding: 0;
  list-style: none;
  background: #fff;
  border: 1px solid #dee2e6;
  border-radius: 0.25rem;
  max-height: 220px;
  overflow-y: auto;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.item-sugerencia-cliente {
  padding: 4px 8px;
  cursor: pointer;
}

.item-sugerencia-cliente:hover,
.item-sugerencia-cliente--activo {
  background-color: #e9f7ef;
}

.observaciones-reserva {
  min-height: 31px;
  height: 31px;
  resize: vertical;
}

.form-label {
  margin-bottom: 0.25rem;
}

hr {
  margin: 0.5rem 0;
}

.reservas-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  width: 100%;
}

.reservas-toolbar-left {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  min-width: 0;
}

.reservas-filtros {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: nowrap;
}

.reservas-filtros .form-select {
  width: 180px;
}

.reservas-fecha-duracion {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: nowrap;
}

.reservas-fecha {
  width: 240px;
}

.duracion-turnos {
  width: 210px;
}

.btn-nueva-reserva-desktop {
  flex: 0 0 auto;
  margin-left: auto;
  white-space: nowrap;
}

.btn-nueva-reserva-mobile {
  display: none !important;
}

/* =========================================================
   TABLET / NOTEBOOK ANGOSTA
   Dos filas máximo + botón flotante
   ========================================================= */
@media (max-width: 1399.98px) {
  .reservas-toolbar {
    align-items: stretch;
  }

  .reservas-toolbar-left {
    flex: 1 1 auto;
    display: grid;
    grid-template-columns: 1fr;
    gap: 0.5rem;
  }

  .reservas-filtros,
  .reservas-fecha-duracion {
    width: 100%;
  }

  .reservas-filtros {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    gap: 0.5rem;
  }

  .reservas-filtros .form-select {
    width: 100%;
    min-width: 0;
  }

  .reservas-fecha-duracion {
    display: grid;
    grid-template-columns: minmax(240px, 1fr) minmax(180px, 210px);
    gap: 0.5rem;
  }

  .reservas-fecha,
  .duracion-turnos {
    width: 100%;
    min-width: 0;
  }

  .btn-nueva-reserva-desktop {
    display: none !important;
  }

  .btn-nueva-reserva-mobile {
    display: inline-flex !important;
    align-items: center;
    justify-content: center;

    position: fixed;
    right: 18px;
    bottom: calc(18px + env(safe-area-inset-bottom));

    width: 54px;
    height: 54px;
    padding: 0;

    border-radius: 50%;
    border: none;

    font-size: 1.75rem;
    font-weight: 700;
    line-height: 1;

    z-index: 1035;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.28);
  }
}

/* =========================================================
   MOBILE
   Dos filas: Deporte/Cancha y Fecha/Duración
   ========================================================= */
@media (max-width: 767.98px) {
  .reservas-toolbar {
    flex-direction: column;
  }

  .reservas-toolbar-left {
    width: 100%;
  }

  .reservas-filtros {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  }

  .reservas-fecha-duracion {
    grid-template-columns: minmax(0, 1.45fr) minmax(125px, 0.8fr);
  }

  .reservas-fecha,
  .duracion-turnos {
    width: 100%;
  }

  .reservas-fecha .btn {
    padding-left: 0.5rem;
    padding-right: 0.5rem;
  }

  .reservas-fecha .form-control {
    min-width: 0;
  }

  .duracion-turnos .btn {
    padding-left: 0.35rem;
    padding-right: 0.35rem;
    white-space: nowrap;
  }
}

@media (max-width: 420px) {
  .reservas-filtros {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  }

  .reservas-fecha-duracion {
    grid-template-columns: minmax(0, 1.4fr) minmax(115px, 0.8fr);
  }

  .reservas-filtros .form-select,
  .reservas-fecha .form-control,
  .duracion-turnos .btn {
    font-size: 0.8rem;
  }

  .btn-nueva-reserva-mobile {
    right: 16px;
    bottom: calc(16px + env(safe-area-inset-bottom));
    width: 52px;
    height: 52px;
  }
}

</style>
